import { useCallback, useEffect, useState } from 'react'
import { api } from '../../api/client'

type Reference = {
  metric: string
  stratum: Record<string, unknown>
  n_people: number
  n_sessions: number
  status: string
  q25: number | null
  q50: number | null
  q75: number | null
}
type Cohort = {
  reference_id: string | null
  reference: {
    version: string
    computed_at: string
    source_fingerprint: string
    references: Reference[]
  } | null
  sessions: number
  silver_ready: number
  session_status: Record<string, number>
  clock_quality: Record<string, number>
  note: string
}

export function AdminCohort() {
  const [data, setData] = useState<Cohort | null>(null)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)
  const load = useCallback(async () => {
    setError('')
    try {
      setData(await api.get<Cohort>('/admin/cohort-reference'))
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'No pudimos consultar la cohorte.',
      )
    }
  }, [])
  useEffect(() => {
    void load()
  }, [load])
  async function upload(file?: File) {
    if (!file) return
    setBusy(true)
    setError('')
    setMessage('')
    try {
      if (file.size > 2_000_000)
        throw new Error('El archivo debe pesar menos de 2 MB.')
      const payload: unknown = JSON.parse(await file.text())
      const result = await api.post<{ created: boolean }>(
        '/admin/cohort-reference',
        payload,
      )
      await load()
      setMessage(
        result.created
          ? 'Referencia importada y versionada.'
          : 'Esta versión ya estaba registrada; no se duplicó.',
      )
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'No pudimos importar la referencia.',
      )
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="space-y-lg min-w-0">
      <header>
        <h1 className="text-2xl font-semibold">Cohorte y calidad</h1>
        <p className="text-text-muted">
          Referencia poblacional descriptiva, no un baremo clínico.
        </p>
      </header>
      {error && (
        <div role="alert" className="banner-err">
          {error}{' '}
          <button className="btn-ghost" onClick={() => void load()}>
            Reintentar
          </button>
        </div>
      )}
      {message && (
        <p role="status" className="banner-ok">
          {message}
        </p>
      )}
      {!data && !error && (
        <p role="status">Consultando inventario y referencia vigente…</p>
      )}
      {data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-md">
            <div className="stat-card">
              <p className="stat-card-label">Sesiones registradas</p>
              <p className="stat-card-value">{data.sessions}</p>
            </div>
            <div className="stat-card">
              <p className="stat-card-label">Silver materializado</p>
              <p className="stat-card-value">{data.silver_ready}</p>
            </div>
            <div className="stat-card">
              <p className="stat-card-label">Estratos de referencia</p>
              <p className="stat-card-value">
                {data.reference?.references.length ?? 0}
              </p>
            </div>
          </div>
          <p className="text-sm text-text-muted">{data.note}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-md">
            {[
              ['Estado operativo', data.session_status],
              ['Calidad del reloj', data.clock_quality],
            ].map(([title, counts]) => (
              <section className="card" key={String(title)}>
                <h2 className="font-semibold">{String(title)}</h2>
                <dl>
                  {Object.entries(counts as Record<string, number>).map(
                    ([k, v]) => (
                      <div className="flex justify-between gap-md" key={k}>
                        <dt className="break-words">{k}</dt>
                        <dd>{v}</dd>
                      </div>
                    ),
                  )}
                </dl>
              </section>
            ))}
          </div>
          <section className="card space-y-md">
            <h2 className="font-semibold">Referencia vigente</h2>
            {data.reference ? (
              <p className="text-sm break-words">
                {data.reference.version} ·{' '}
                {new Date(data.reference.computed_at).toLocaleString('es-AR')} ·
                fuente {data.reference.source_fingerprint}
              </p>
            ) : (
              <p>
                No se importó una corrida verificada. Los informes muestran
                referencia en construcción.
              </p>
            )}
            <p className="text-sm text-text-muted">
              Generá el JSON desde Gold con{' '}
              <code>
                python -m scripts.gold_reference --gold-dir DIRECTORIO
                --identity-map identidad.json --out referencia.json
              </code>
              . Cada persona confirmada aporta un agregado por estrato; no se
              mezclan mecánicas, ventanas ni versiones. Distribuciones: ≥5
              personas y ≥10 sesiones. Se muestran cuantiles, no percentiles
              individuales interpolados.
            </p>
            <label className="block text-sm font-medium">
              Importar referencia agregada
              <input
                aria-label="Archivo JSON de referencia"
                type="file"
                accept="application/json,.json"
                disabled={busy}
                className="input mt-sm block w-full max-w-lg"
                onChange={(e) => void upload(e.target.files?.[0])}
              />
            </label>
            {busy && <p role="status">Validando e importando la referencia…</p>}
          </section>
          <section className="space-y-md">
            <h2 className="font-semibold">Cobertura por estrato</h2>
            {data.reference?.references.map((r, i) => (
              <article className="card min-w-0" key={i}>
                <div className="flex flex-wrap justify-between gap-sm">
                  <h3 className="font-semibold">{r.metric}</h3>
                  <span className={r.status === 'available' ? 'tag-ok' : 'tag'}>
                    {r.status === 'available'
                      ? 'Referencia disponible'
                      : 'En construcción'}
                  </span>
                </div>
                <p className="text-sm">
                  {r.n_people} personas · {r.n_sessions} sesiones
                </p>
                <p className="text-xs text-text-muted break-words">
                  {Object.entries(r.stratum)
                    .map(([k, v]) => `${k}: ${String(v)}`)
                    .join(' · ')}
                </p>
                {r.q50 != null && (
                  <p className="text-sm">
                    Mediana {r.q50.toLocaleString('es-AR')} · mitad central{' '}
                    {r.q25?.toLocaleString('es-AR')} a{' '}
                    {r.q75?.toLocaleString('es-AR')}
                  </p>
                )}
              </article>
            ))}
          </section>
        </>
      )}
    </div>
  )
}
